/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React, { useState } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import Paper from "@mui/material/Paper";
import { Box, Checkbox, FormControlLabel, MenuItem, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useForm } from "@/src/provider/Form/FormProvider";
import Group from "@/src/components/Wizard/Group";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import { isRequired } from "@/src/services/validationRules";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import { formValue } from "@/src/services/formServiceFunctions";
import PensionsDivorceModal from "@/src/components/FormInfoModal/PensionsDivorceModal";
import Typography from "@mui/material/Typography";
import { translate } from "@/src/services/translations/translations";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import wizardStyle from "@/src/components/Form/wizardStyle";
import InfoIconButton from "@/src/components/Button/InfoIconButton";

type PensionsProps = {
  submitProps: FormSubmitProps;
};
export default function Pensions({ submitProps }: PensionsProps) {
  const [pensionsModalOpen, setPensionsModalOpen] = useState("");
  const { values, handleChange, handleSubmit } = useForm();
  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Finanzen
          </Typography>
          <Typography variant={"h4"} sx={wizardStyle.subheadline}>
            {translate("divorce.pensionsExcluded.label", values)}
          </Typography>
          <Group
            type="radio"
            name="pensionsExcluded"
            question={translate("divorce.pensionsExcluded.question", values)}
            modalContent={
              <>
                <p>
                  Bei einer Scheidung führt das Gericht immer den Versorgungsausgleich durch. Hierbei geht es um die Teilung der Rentenansprüche der Eheleute
                  aus der Ehezeit. Im Normalfall geht es um den Ausgleich von 2 Rentenansprüchen: je eine gesetzliche Rentenversicherung pro Ehepartner. Sollten
                  Sie jedoch zum Beispiel zusätzlich eine private Rentenversicherung (betriebliche Altersvorsorge oder Riesterrente) haben, geben Sie bei der
                  Anzahl Rentenversicherungen eine 2 an.
                </p>
                <p>Der Ausgleich ist gesetzlich vorgeschrieben, Sie können aber gemeinsam mit Ihrem Ehepartner per Notar auf den Ausgleich verzichten.</p>
              </>
            }
            options={[
              {
                labelText: translate("divorce.pensionsExcluded.options.no.label", values),
                labelIcon: <CheckIcon />,
                labelIconActive: <CheckIcon active />,
                value: "no" /* no because NOT pensionsExcluded */,
              },
              {
                labelText: translate("divorce.pensionsExcluded.options.yes.label", values),
                labelIcon: <CrossIcon />,
                labelIconActive: <CrossIcon active />,
                value: "yes" /* yes because pensionsExcluded is TRUE */,
                optionHintText: "Achtung: Der Ausgleich ist gesetzlich vorgeschrieben. Ein Ausschluss ist nur per Notar möglich.",
              },
            ]}
            validators={[
              {
                validator: isRequired,
                message: "Bitte geben Sie an ob der Versorgungsausgleich durchgeführt wird",
              },
            ]}
          />
          <Grid container>
            {values.pensionsExcluded === "no" && (
              <>
                <Grid size={10}>
                  <ValidatorSelectField
                    label={translate("divorce.numberPensions.label", values)}
                    name="numberPensions"
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte geben Sie die Anzahl Ihrer Rentenversicherungen an",
                      },
                    ]}
                  >
                    <MenuItem value={"0"}>{translate("divorce.numberPensions.options.0.label", values)}</MenuItem>
                    <MenuItem value={"1"}>{translate("divorce.numberPensions.options.1.label", values)}</MenuItem>
                    <MenuItem value={"2"}>{translate("divorce.numberPensions.options.2.label", values)}</MenuItem>
                    <MenuItem value={"3"}>{translate("divorce.numberPensions.options.3.label", values)}</MenuItem>
                    <MenuItem value={"4"}>{translate("divorce.numberPensions.options.4.label", values)}</MenuItem>
                    <MenuItem value={"5"}>{translate("divorce.numberPensions.options.5.label", values)}</MenuItem>
                  </ValidatorSelectField>
                </Grid>
                <Grid size={2}>
                  <Stack
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <InfoIconButton onClick={() => setPensionsModalOpen("client")} />
                  </Stack>
                </Grid>
                <Grid size={10}>
                  <ValidatorSelectField
                    label={translate("divorce.numberPensionsPartner.label", values)}
                    name="numberPensionsPartner"
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte geben Sie die Anzahl der Rentenversicherungen Ihres Partners an",
                      },
                    ]}
                    helperText="Sollten Sie hier unsicher sein, geben Sie hier zunächst einfach den Standard “1” an."
                  >
                    <MenuItem value={"0"}>{translate("divorce.numberPensionsPartner.options.0.label", values)}</MenuItem>
                    <MenuItem value={"1"}>{translate("divorce.numberPensionsPartner.options.1.label", values)}</MenuItem>
                    <MenuItem value={"2"}>{translate("divorce.numberPensionsPartner.options.2.label", values)}</MenuItem>
                    <MenuItem value={"3"}>{translate("divorce.numberPensionsPartner.options.3.label", values)}</MenuItem>
                    <MenuItem value={"4"}>{translate("divorce.numberPensionsPartner.options.4.label", values)}</MenuItem>
                    <MenuItem value={"5"}>{translate("divorce.numberPensionsPartner.options.5.label", values)}</MenuItem>
                  </ValidatorSelectField>
                </Grid>
                <Grid size={2}>
                  <Stack
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      height: "70%",
                    }}
                  >
                    <InfoIconButton onClick={() => setPensionsModalOpen("partner")} />
                  </Stack>
                </Grid>
                <Grid sx={wizardStyle.formSpacer} size={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="pensionsQuestions"
                        name="pensionsQuestions"
                        checked={!!formValue(values, "pensionsQuestions")}
                        onChange={handleChange}
                        value="pensionsQuestions"
                        color="primary"
                      />
                    }
                    label={translate("divorce.pensionsQuestions.label", values)}
                  />
                </Grid>
                <PensionsDivorceModal personType={pensionsModalOpen} onClose={() => setPensionsModalOpen("")} open={!!pensionsModalOpen} />
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
